import { Alert, Button, Col, FormGroup, Input, Row } from "reactstrap";
import { useState, useMemo, ChangeEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";

import useInviteUser from "../../../api/api-hooks/tenantUsers/useInviteUser";
import useAllClouds from "../../../api/api-hooks/cloud/useAllClouds";
import PermissionList from "../../../components/CRM/PermissionList";
import { callFunctionIfExists } from "../../../utils/GeneralUtils";
import useCloud from "../../../api/api-hooks/cloud/useCloud";
import { newToastEvent } from "../../../utils/ToastUtil";
import AppSpinner from "../../../elements/AppSpinner";
import StrUtils from "../../../utils/StrUtils";
import SelectCloud from "./SelectCloud";
import AppDrawer from "../AppDrawer";

interface SupportInviteUserDrawerProps {
  open: boolean;
  person?: any;
  onClose: () => void;
  onSuccess?: () => void;
}

type Page = "CloudPage" | "InviteUserPage";

const SupportalInviteUserDrawer = ({
  open,
  person,
  onClose,
  onSuccess,
}: SupportInviteUserDrawerProps) => {
  const { allClouds, allCloudsLoading } = useAllClouds();
  const [validEmail, setValidEmail] = useState<boolean>(true);
  const [page, setPage] = useState<Page>("CloudPage");
  const [tenantID, setTenantID] = useState<string>("");
  const [permissionID, setPermissionID] = useState<string>("");
  const [email, setEmail] = useState<string>(person?.PrimaryContactEmail || "");
  const { cloudPermissions } = useCloud(tenantID);
  const queryClient = useQueryClient();

  const { inviteUser, invitingUser, inviteUserError, clearError } =
    useInviteUser();

  const filterClouds = useMemo(() => {
    if (person?.Tenants) {
      const pClouds = person?.Tenants.map((tenant: any) => tenant.TenantID);

      return (
        allClouds?.filter(
          (cloud) => !cloud.Deleted && !pClouds.includes(cloud.id)
        ) ?? []
      );
    }
    return allClouds?.filter((cloud) => !cloud.Deleted) ?? [];
  }, [allClouds, person]);

  const handleTogglePage = (page: Page) => () => {
    setPage(page);
  };

  const handleClose = () => {
    setPage("CloudPage");
    setTenantID("");
    setPermissionID("");
    setEmail(person?.PrimaryContactEmail || "");
    setValidEmail(true);
    clearError();

    onClose();
  };

  const handleInviteUser = () => {
    inviteUser({
      email,
      permission: permissionID,
      onSuccess() {
        handleClose();
        callFunctionIfExists(onSuccess);
        newToastEvent("success", "Successfully invited user");
        queryClient.invalidateQueries({
          queryKey: ["profiles", person?.id],
        });
      },
      supportTargetCloudID: tenantID, // Support Request - Provide Cloud ID!
    });
  };

  const handleValidateEmail = () => {
    if (!email) return false;

    return StrUtils.validateEmail(email);
  };

  return (
    <AppDrawer
      open={open}
      onClose={handleClose}
      headerTitle="Invite User"
      headerOptions={
        <>
          {page === "InviteUserPage" && (
            <Button size="sm" onClick={handleTogglePage("CloudPage")}>
              Previous
            </Button>
          )}
          <Button
            size="sm"
            color="primary"
            style={{ width: 80 }}
            onClick={
              page === "InviteUserPage"
                ? handleInviteUser
                : handleTogglePage("InviteUserPage")
            }
            disabled={
              invitingUser || page === "CloudPage"
                ? !tenantID
                : !(permissionID && email && tenantID && validEmail)
            }
          >
            {invitingUser ? (
              <AppSpinner color="white" />
            ) : page === "InviteUserPage" ? (
              "Save"
            ) : (
              "Next"
            )}
          </Button>
        </>
      }
      bodyCSS="pt-3"
      body={
        <>
          {page === "CloudPage" &&
            (allCloudsLoading ? (
              <div>
                <Row className="align-items-center">
                  <Col style={{ maxWidth: "calc(100% - 62px)" }}>
                    <h4 className="mb-1">Hang Tight...</h4>
                    <p className="text-muted mb-0">
                      We&apos;re fetching your clouds.
                    </p>
                  </Col>
                </Row>
              </div>
            ) : filterClouds && filterClouds.length ? (
              <SelectCloud
                clouds={filterClouds}
                cloudsLoading={allCloudsLoading}
                setTenantID={setTenantID}
                tenantID={tenantID}
              />
            ) : (
              "No Clouds! :("
            ))}
          {page === "InviteUserPage" && (
            <>
              {inviteUserError && (
                <Alert className="mb-4" color="danger">
                  {inviteUserError
                    ? inviteUserError
                    : "Invalid email provided."}
                </Alert>
              )}
              <FormGroup>
                <h5>Email</h5>
                <Input
                  placeholder="Enter email"
                  type="email"
                  required={true}
                  disabled={!!person?.PrimaryContactEmail || false}
                  value={email}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setEmail(e.target.value);
                    setValidEmail(true);
                  }}
                  onBlur={handleValidateEmail}
                />
              </FormGroup>
              {!validEmail && email && (
                <Alert className="mb-4" color="danger">
                  Invalid email provided.
                </Alert>
              )}
              <h5>Permissions</h5>
              <PermissionList
                permissions={cloudPermissions.filter(
                  (permission) => permission.Name !== "Cloud Owner"
                )}
                onChange={setPermissionID}
                selectedPermission={permissionID}
              />
            </>
          )}
        </>
      }
    />
  );
};

export default SupportalInviteUserDrawer;
